import { useTranslations } from 'next-intl'
import { MapPin, ExternalLink } from 'lucide-react'

interface PropertyMapProps {
  googleMapUrl?: string | null
  address: string | null
  propertyName: string
  latitude?: number | null
  longitude?: number | null
}

/**
 * Extract coordinates or embed URL from google_map_url
 * Supports formats:
 * - https://maps.google.com/maps?q=LAT,LNG
 * - https://www.google.com/maps/place/.../@LAT,LNG,...
 * - https://goo.gl/maps/...
 * - Direct embed URLs
 */
function getEmbedUrl(googleMapUrl: string | null | undefined, latitude?: number | null, longitude?: number | null): string {
  // Use lat/lng from DB first (most reliable)
  if (latitude && longitude) {
    return `https://maps.google.com/maps?q=${latitude},${longitude}&z=15&output=embed`
  }

  if (!googleMapUrl) {
    return ''
  }

  // Try to extract lat,lng from URL
  const coordMatch = googleMapUrl.match(/@(-?\d+\.?\d*),(-?\d+\.?\d*)/)
  if (coordMatch) {
    return `https://maps.google.com/maps?q=${coordMatch[1]},${coordMatch[2]}&z=15&output=embed`
  }

  // Try q=lat,lng format
  const qMatch = googleMapUrl.match(/[?&]q=(-?\d+\.?\d*),(-?\d+\.?\d*)/)
  if (qMatch) {
    return `https://maps.google.com/maps?q=${qMatch[1]},${qMatch[2]}&z=15&output=embed`
  }

  // Try place format
  const placeMatch = googleMapUrl.match(/place\/([^/]+)/)
  if (placeMatch) {
    // Invariant: the regex has one capture group, so [1] exists when the match is non-null
    return `https://maps.google.com/maps?q=${encodeURIComponent(placeMatch[1]!)}&z=15&output=embed`
  }

  // Fallback: use the URL directly in embed
  return `https://maps.google.com/maps?q=${encodeURIComponent(googleMapUrl)}&z=15&output=embed`
}

export function PropertyMap({ googleMapUrl, address, propertyName, latitude, longitude }: PropertyMapProps) {
  const t = useTranslations('booking.propertyDetail')
  const embedUrl = getEmbedUrl(googleMapUrl, latitude, longitude)

  if (!embedUrl) return null

  const mapsLink = googleMapUrl
    || (latitude && longitude ? `https://www.google.com/maps?q=${latitude},${longitude}` : null)

  return (
    <section>
      <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
        {t('location')}
      </h2>

      <div className="rounded-lg overflow-hidden border dark:border-gray-700">
        <iframe
          src={embedUrl}
          width="100%"
          height="350"
          style={{ border: 0 }}
          allowFullScreen
          loading="lazy"
          referrerPolicy="no-referrer-when-downgrade"
          title={`${propertyName} location`}
          className="w-full"
        />
      </div>

      <div className="flex items-center justify-between mt-3">
        {address && (
          <div className="flex items-center gap-1.5 text-sm text-gray-600 dark:text-gray-400">
            <MapPin className="h-4 w-4 shrink-0" />
            <span>{address}</span>
          </div>
        )}
        {mapsLink && (
          <a
            href={mapsLink}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-1 text-sm text-blue-600 dark:text-blue-400 hover:underline shrink-0"
          >
            {t('openInGoogleMaps')}
            <ExternalLink className="h-3.5 w-3.5" />
          </a>
        )}
      </div>
    </section>
  )
}
